import React, { memo, useEffect, useState } from 'react';
import { FormControl as MuiFormControl, FormHelperText, IconButton, InputLabel } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { withStyles } from '@mui/styles';
import { isEqual, find, size, clone, isArray, isString, isUndefined, isEmpty } from 'lodash';
import { ERROR_THEME_COLOR } from '../../../const';
import Checkbox from '../Checkbox/Checkbox';
import MenuItem from '../MenuItem/MenuItem';
import Select from '../Select/Select';

const formControlStyle = {
  root: {
    '& .MuiInputLabel-root': {
      '& .MuiInputLabel-asterisk': {
        color: ERROR_THEME_COLOR,
      },
    },
    '& .MuiInput-underline:before': {
      borderBottom: '1px solid rgb(224, 224, 224)',
    },
  },
};

const FormControl = withStyles(formControlStyle)(MuiFormControl);

const clearButtonStyle = {
  position: 'absolute',
  marginTop: '8px',
  right: 0,
};

const getOptionVal = (opt) => {
  if (isString(opt.label)) {
    return opt.label.toUpperCase();
  }

  return opt.value;
};

const initStateValue = ({ multiple, inputValue }) => {
  if (multiple) {
    return isArray(inputValue) ? inputValue : [];
  }

  return isUndefined(inputValue) ? '' : inputValue;
};

const RenderSelect = ({
  meta: { touched, error },
  label,
  disabled,
  required,
  multiple = false,
  readOnly,
  floatingLabelText,
  className,
  fullWidth,
  nullable,
  disabledOptions,
  input: { name, onChange, value: inputValue },
  displayEmpty = false,
  showCheckboxes = false,
  clearable,
  options,
  defaultSorted,
  withSelectAll = false,
  isEmptyByDefault = false,
}) => {
  const [value, setValue] = useState(initStateValue({ multiple, inputValue }));
  const optionsValues = options && options.map((option) => option.value);

  useEffect(() => {
    if (inputValue !== value) {
      setValue(initStateValue({ multiple, inputValue }));
    }
  }, [inputValue]);

  useEffect(() => {
    if (multiple && withSelectAll && value.length === 0 && !isEmptyByDefault) {
      setValue(optionsValues);
    }
  }, []);

  const handleChange = (event) => {
    const defaultValue = multiple ? [] : '';
    const { value: targetValue } = event.target;

    let forwardValue = !isUndefined(targetValue) ? targetValue : defaultValue;

    if (multiple && withSelectAll) {
      if (targetValue.includes('all')) {
        forwardValue = value.length === options.length ? [] : optionsValues;
      }
    }

    onChange(forwardValue);
    setValue(forwardValue);
  };

  const showClearButton = () => value !== null && (multiple ? size(value) > 0 : value !== '') && !readOnly && clearable;

  const getSelectWidth = () => {
    let width = 'auto';

    if (showClearButton() && fullWidth) {
      width = '80%';
    } else if (fullWidth) {
      width = '100%';
    }

    return width;
  };

  const normalizeOptions = (newOptions) => {
    const labels = newOptions.map((option) => find(options, { value: option }));

    return labels
      .filter((item) => item)
      .map((item) => item.label)
      .join(', ');
  };

  const errorText = touched && error ? error : '';
  const labelText = floatingLabelText || label || '';
  const optionsArray = size(options) ? [...options] : [];
  let children = [];

  if (optionsArray) {
    if (value !== '' && !multiple) {
      const inOptions = optionsArray.find((item) => item.value === value);

      if (!inOptions) {
        const inDisabledOptions = size(disabledOptions) ? disabledOptions.find((item) => item.value === value) : null;

        optionsArray.push(
          inDisabledOptions
            ? {
                value: inDisabledOptions.value,
                label: inDisabledOptions.label,
              }
            : {
                value,
                label: value,
              },
        );
      }
    }

    let localOptions = clone(optionsArray);

    if (!defaultSorted) {
      localOptions = localOptions.sort((a, b) => {
        const alc = getOptionVal(a);
        const blc = getOptionVal(b);

        if (alc > blc) {
          return 1;
        }

        if (alc < blc) {
          return -1;
        }

        return 0;
      });
    }

    children = localOptions.map((item, index) => (
      <MenuItem key={index} value={item.value} disabled={item.disabled} title={item.label} fullWidth={fullWidth}>
        {multiple && showCheckboxes && <Checkbox checked={value.includes(item.value)} />}
        {item.label}
      </MenuItem>
    ));
  }

  if (withSelectAll && multiple) {
    children = [
      <MenuItem key="select-all" value="all" fullWidth={fullWidth}>
        <Checkbox checked={value.length === options.length} />
        All
      </MenuItem>,
      ...children,
    ];
  }

  if (nullable) {
    children.unshift(<MenuItem key="nullable" value={null} fullWidth={fullWidth} />);
  }

  const isShowClearButton = showClearButton();
  const selectWidth = getSelectWidth();
  const multiProps = multiple
    ? {
        renderValue: normalizeOptions,
      }
    : {};

  return (
    <div data-field-name={`${name}`} className="contents">
      <FormControl fullWidth={fullWidth} error={!!errorText}>
        <InputLabel shrink required={required}>
          {labelText}
        </InputLabel>
        <Select
          value={value}
          className={className + (isShowClearButton ? ' clearable' : '')}
          onChange={handleChange}
          multiple={multiple}
          disabled={disabled || readOnly}
          style={{ width: selectWidth }}
          error={!!errorText}
          displayEmpty={displayEmpty}
          {...multiProps}
          // renderValue={selected => multiple ? this.normalizeOptions(selected) : selected}
        >
          {children}
        </Select>
        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
        {isShowClearButton ? (
          <IconButton
            size="large"
            style={clearButtonStyle}
            className="resetOwnerButton"
            onClick={() =>
              handleChange({
                target: {
                  value: withSelectAll ? [] : multiple ? optionsValues : null,
                },
              })
            }
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </FormControl>
    </div>
  );
};

const checkPropsChange = (props, nextProps) =>
  nextProps.input.value === props.input.value &&
  !isEqual(nextProps.meta, props.meta) &&
  !isEqual(nextProps.options, props.options) &&
  !isEqual(nextProps.readOnly, props.readOnly);

export default memo(RenderSelect, checkPropsChange);
